import {
  DropzoneField,
  DropzoneReadonly,
  FiFileText,
  Flex,
  FormLabel,
  Grid,
  GridItem,
  ReadOnlyField,
  useFormContext,
} from '@agrotoken-tech/ui';
import { useTranslation } from '@bcba/react-hooks';
import { FileMimeType, fileMimeTypes } from '@bcba/types';
import { ReactNode, useEffect } from 'react';
import { getUrlFileFromAws } from '../../../services/aws/getUrlFileFromAws';
import {
  formatDateToShortFormat,
  unixTimestampToDate,
} from '@bcba/common-utils';
import { getS3SignedUrl } from '../../../services/documents/getS3SignedUrl';
import { S3FileUploader } from '@bcba/frontend-utils';

interface Props {
  fieldPrefix: string;
  label: string;
  deleteButton?: ReactNode;
  hideDelete?: boolean;
}

export const EquityInfoDocument = ({
  fieldPrefix,
  label,
  deleteButton = null,
  hideDelete = false,
}: Props) => {
  const { t } = useTranslation();
  const { setValue, watch, resetField } = useFormContext();

  const documentUuidKey = `${fieldPrefix}.documentUuid`;
  const uuidKey = `${fieldPrefix}.uuid`;
  const fileKey = `${fieldPrefix}.file`;
  const hasFileUploaded = watch(`${fieldPrefix}.uuid`);
  const expirationDateValue = watch(`${fieldPrefix}.expirationDate`);

  const handleDeleteFileClick = () => {
    setValue(uuidKey, undefined, { shouldDirty: true });
    setValue(fileKey, undefined, { shouldDirty: true });
  };

  return (
    <Flex direction="column" gap={6}>
      <Flex direction="column">
        <Flex justify="space-between" align="center">
          <FormLabel>{label}</FormLabel>
          {deleteButton}
        </Flex>
        {hasFileUploaded ? (
          <DropzoneReadonly
            fileName={watch(`${fieldPrefix}.originalName`)}
            label={''}
            onClick={async () => {
              const url = await getUrlFileFromAws(watch(documentUuidKey));
              window.open(url, '_blank');
            }}
            leftIcon={FiFileText}
            onDelete={hideDelete ? undefined : () => handleDeleteFileClick()}
          />
        ) : (
          <S3FileUploader
            fieldPrefix={`${fieldPrefix}.file.s3TempKey`}
            label=""
            getS3PreSignedUrl={getS3SignedUrl}
            onFileUploadSuccess={async (fileName, mimeType, s3TempKey) => {
              setValue(`${fieldPrefix}.file.name`, fileName);
              setValue(
                `${fieldPrefix}.file.mimeType`,
                mimeType as FileMimeType,
              );
              setValue(`${fieldPrefix}.file.s3TempKey`, s3TempKey);
              setValue(`${fieldPrefix}.keepOriginal`, false, {
                shouldDirty: true,
              });
            }}
            onDeleteClick={() => {
              resetField(`${fieldPrefix}.file.s3TempKey`);
              resetField(`${fieldPrefix}.file.name`);
              resetField(`${fieldPrefix}.file.mimeType`);
            }}
          />
        )}
      </Flex>
      <Grid
        templateColumns={{ base: 'repeat(1, 1fr)', md: 'repeat(2, 1fr)' }}
        gap={6}
      >
        <GridItem>
          <ReadOnlyField
            label={t('common.documentId.label')}
            value={
              watch(`${fieldPrefix}.bcIdentifier`)
                ? watch(`${fieldPrefix}.bcIdentifier`)
                : t('common.toBeCompletedByBolsa')
            }
          />
        </GridItem>
        <GridItem>
          <ReadOnlyField
            label={t('common.expirationDate.label')}
            value={
              expirationDateValue
                ? formatDateToShortFormat(
                    unixTimestampToDate(expirationDateValue),
                  )
                : t('common.toBeCompletedByBolsa')
            }
          />
        </GridItem>
      </Grid>
    </Flex>
  );
};

export default EquityInfoDocument;
